import { useImperativeHandle, useCallback } from 'react';
import { InputRef } from '@/shared/types/input';

interface UseInputRefProps {
  ref: React.ForwardedRef<InputRef>;
  currentValue: string;
  setValue: (value: string) => void;
  isControlled: boolean;
  inputId?: string;
}

export const useInputRef = ({
  ref,
  currentValue,
  setValue,
  isControlled,
  inputId,
}: UseInputRefProps) => {
  const getInputElement = useCallback(() => {
    const id = inputId || 'default-input';
    return document.querySelector(`input[data-input-id="${id}"]`) as HTMLInputElement;
  }, [inputId]);

  const focus = useCallback(() => {
    const inputElement = getInputElement();
    inputElement?.focus();
  }, [getInputElement]);

  const blur = useCallback(() => {
    const inputElement = getInputElement();
    inputElement?.blur();
  }, [getInputElement]);

  const clear = useCallback(() => {
    setValue('');
  }, [setValue]);

  const getValue = useCallback(() => {
    return currentValue;
  }, [currentValue]);

  const setValueFromRef = useCallback(
    (newValue: string) => {
      setValue(newValue);
    },
    [setValue],
  );

  useImperativeHandle(
    ref,
    () => ({
      getValue,
      setValue: setValueFromRef,
      focus,
      blur,
      clear,
      getInputElement,
    }),
    [getValue, setValueFromRef, focus, blur, clear, getInputElement],
  );

  return {
    focus,
    blur,
    clear,
    getValue,
    setValueFromRef,
  };
};
